import React, { useEffect, useState } from 'react';

import { State } from 'model/State';
import { styled } from 'styled-components';
import { Dimensions } from 'components/visuals';
import { colors } from '@atlaskit/theme';
import { FormattedMessage } from 'react-intl';
import { messages } from './messages';
import Button from '@atlaskit/button';
import { useDispatch, useSelector } from 'react-redux';
import { getCurrentMACContactEmail, selectDetails, selectUpdatedSid } from './duck/selectors';
import SectionMessage from '@atlaskit/section-message';
import t from 'modules/i18n/intl';
import { updateServerIdAction } from './duck/actions/accountId';
import { redirect } from 'redux-first-router';
import * as preselectedLicense from 'modules/mac/duck/actions/licensesPreselected';
import MACPageHeader from './components/macPageHeader';
import { PageH3Header, StyledButton } from './components/styledComponents';
import SidInstructions from './components/SidInstuctions';

const ServerIdDetailsWrapper = styled.div`
  padding: ${Dimensions.units(2)}px;
`;

const DetailLineWrapper = styled.div`
  display: flex;
  margin-bottom: ${Dimensions.units(4)}px;
`;

const InstanceOptionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const DetailsValueLabelWrapper = styled.div`
  color: ${colors.N400};
  width: ${Dimensions.units(25)}px;
  margin-right: ${Dimensions.units(2.5)}px;
  text-align: end;
`;

const DetailsValueWrapper = styled.div`
  width: ${Dimensions.units(6.25)}%;
`;

const SidExample = styled.p`
  font-size: ${Dimensions.units(1.5)}px;
  color: ${colors.N100};
`;

const ServerIdInstructionsWrapper = styled.div`
  margin-left: ${Dimensions.units(27.5)}px;
  display: flex;
  flex-direction: column;
`;

const ButtonsWrapper = styled.div`
  margin-top: ${Dimensions.units(2.5)}px;
  margin-left: ${Dimensions.units(27.5)}px;
  display: flex;
`;

const MessageWrapper = styled.div`
  width: ${Dimensions.units(7.5)}%;
`;

const DetailLine = ({ labelText, dataCy = '', children }) => {
  return (
    <DetailLineWrapper {...(dataCy ? { 'data-cy': dataCy } : {})}>
      <DetailsValueLabelWrapper>
        <FormattedMessage {...labelText} />
      </DetailsValueLabelWrapper>
      {children}
    </DetailLineWrapper>
  );
};

export const UpdateServerIdPage = () => {
  const dispatch = useDispatch();
  const accountId = useSelector((state) => (state as State).location.payload.accountId);
  const userEmail = useSelector(getCurrentMACContactEmail);
  const [invalidServerIdError, setInvalidServerIdError] = useState(false);
  const [unauthorisedError, setUnauthorisedError] = useState(false);
  const [serverId, setServerId] = useState('');
  const [selectedInstanceOption, setSelectedInstanceOption] = useState('upAndRunning');
  const [isServerIdValid, setIsServerIdValid] = useState(true);
  const [successfullyUpdatedSid, setSuccessfullyUpdatedSid] = useState(false);
  const updateSidResponse = useSelector(selectUpdatedSid);

  useEffect(() => {
    if (updateSidResponse) {
      if (updateSidResponse._result === 'Success') {
        setSuccessfullyUpdatedSid(true);
        setTimeout(() => {
          dispatch(redirect(preselectedLicense.licensePreselectedRoute(accountId, {})));
        }, 1000);
      } else if (updateSidResponse._result === 'Server Id is invalid') {
        setIsServerIdValid(false);
        setInvalidServerIdError(true);
      }
    }
  }, [updateSidResponse]);

  const detailsData: any = useSelector((state) => selectDetails(state as State, accountId));
  const accountDetails = detailsData && detailsData === 'Access denied' ? null : detailsData;

  const handleClickSave = () => {
    if (
      accountDetails?.merchandise.productPackage.productType === 'HOSTED' ||
      !accountDetails?.contactRelationships?.some(
        ({ contact }) => contact.contactDetails?.email === userEmail,
      )
    ) {
      setUnauthorisedError(true);
    } else if (!serverId || !isServerIdValid) {
      setInvalidServerIdError(true);
    } else {
      dispatch(updateServerIdAction(accountId, serverId));
    }
  };

  return (
    <>
      <MACPageHeader />
      {invalidServerIdError && (
        <MessageWrapper>
          <SectionMessage
            appearance="error"
            title={t('mac.server-id-page.invalid.server.id.title')}
          >
            <FormattedMessage {...messages.invalidServerIdMessage} />
          </SectionMessage>
        </MessageWrapper>
      )}
      {unauthorisedError && (
        <MessageWrapper>
          <SectionMessage appearance="error">
            <FormattedMessage {...messages.unauthorisedMessage} />
          </SectionMessage>
        </MessageWrapper>
      )}
      {successfullyUpdatedSid && (
        <MessageWrapper>
          <SectionMessage appearance="success">
            <FormattedMessage {...messages.successfullyUpdatedSid} />
          </SectionMessage>
        </MessageWrapper>
      )}
      <PageH3Header>
        <FormattedMessage {...messages.serverIdPageTitle} />
      </PageH3Header>
      <FormattedMessage
        {...messages.serverIdPageSubtitle}
        values={{ productDescription: accountDetails?.merchandise?.productPackage?.description }}
      />
      <hr />
      <ServerIdDetailsWrapper>
        <DetailLine dataCy="product-description" labelText={messages.productLabel}>
          <DetailsValueWrapper>
            {accountDetails?.merchandise?.productPackage?.description}
          </DetailsValueWrapper>
        </DetailLine>
        <DetailLine dataCy="organisation-name" labelText={messages.organization}>
          <DetailsValueWrapper>
            {accountDetails?.latestPeriod.license?.licenseDetails?.organisation}
          </DetailsValueWrapper>
        </DetailLine>
        <DetailLine labelText={messages.instancelabel}>
          <InstanceOptionsWrapper>
            <div>
              <input
                type="radio"
                id="instanceTypeRunning"
                name="instance"
                value="upAndRunning"
                checked={selectedInstanceOption === 'upAndRunning'}
                onChange={() => setSelectedInstanceOption('upAndRunning')}
              />
              <label htmlFor="instanceType">
                <FormattedMessage {...messages.upAndRunningInstance} />
              </label>
            </div>
            <div>
              <input
                type="radio"
                id="instanceTypeNotInstalled"
                name="instance"
                value="notInstalledYet"
                checked={selectedInstanceOption === 'notInstalledYet'}
                onChange={() => setSelectedInstanceOption('notInstalledYet')}
              />
              <label htmlFor="instanceTypeNotInstalled">
                <FormattedMessage {...messages.notInstalledYetInstance} />
              </label>
            </div>
          </InstanceOptionsWrapper>
        </DetailLine>
        <DetailLine labelText={messages.serverIdLabel}>
          <div>
            <input
              type="text"
              name="sidInput"
              id="sidInput"
              data-cy="sidInput"
              onChange={(e) => {
                setInvalidServerIdError(false);
                setUnauthorisedError(false);
                setServerId(e.target.value);
              }}
            />
            <SidExample>ABCD-1234-EFGH-5678</SidExample>
          </div>
        </DetailLine>
        <ServerIdInstructionsWrapper>
          <SidInstructions
            selectedInstanceOption={selectedInstanceOption}
            productKey={accountDetails?.merchandise?.productPackage?.key}
          />
        </ServerIdInstructionsWrapper>
        <ButtonsWrapper>
          <StyledButton
            $marginRight={`${Dimensions.units(1.5)}px`}
            appearance="primary"
            onClick={handleClickSave}
            isDisabled={invalidServerIdError || unauthorisedError}
            data-cy="btn-save-server-id"
          >
            <FormattedMessage {...messages.addNewContactModalSave} />
          </StyledButton>
          <Button
            onClick={() => {
              dispatch(redirect(preselectedLicense.licensePreselectedRoute(accountId)));
            }}
            appearance="subtle"
          >
            <FormattedMessage {...messages.addNewContactModalCancel} />
          </Button>
        </ButtonsWrapper>
      </ServerIdDetailsWrapper>
    </>
  );
};
